import React, { useState } from "react";
import { X, CalendarClock, MapPin, Loader2, AlertTriangle } from "lucide-react";
import { createAppointment, createIntervention } from "../../../api";
import { useToast } from "../../../hooks/use-toast";
import { overlapText } from "../../../hooks/useHolidays";

const inputCls = "w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand";
const fmt = (iso) => new Date(iso).toLocaleString("fr-FR", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });

const QuickCreate = ({ date, holiday, prospects, team, onClose, onCreated }) => {
  const [kind, setKind] = useState("rdv");
  const [loading, setLoading] = useState(false);
  const [rdv, setRdv] = useState({ titre: "", prospect_id: "", notes: "", push_dolibarr: true });
  const [intv, setIntv] = useState({ client_nom: "", adresse: "", ville: "", prestation: "", collaborateurs: [], notify_client: false });
  const { toast } = useToast();

  const toggleCollab = (t) => setIntv((f) => ({
    ...f,
    collaborateurs: f.collaborateurs.some((c) => c.id === t.id) ? f.collaborateurs.filter((c) => c.id !== t.id) : [...f.collaborateurs, { id: t.id, nom: t.nom, login: t.login }],
  }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (kind === "rdv") {
        const r = await createAppointment({ ...rdv, date });
        toast({ title: "RDV planifié ✓", description: r.data.dolibarr_event_id ? `Dolibarr #${r.data.dolibarr_event_id}` : r.data.dolibarr_error ? "Dolibarr non synchronisé (nouvel essai auto)" : undefined });
      } else {
        const r = await createIntervention({ ...intv, date });
        toast({ title: "Intervention planifiée ✓", description: r.data.dolibarr_event_id ? `Dolibarr #${r.data.dolibarr_event_id}` : r.data.dolibarr_error ? "Dolibarr non synchronisé (nouvel essai auto)" : undefined });
        const ov = overlapText(r.data.warnings);
        if (ov) toast({ title: "⚠️ Chevauchement d'interventions", description: ov, variant: "destructive" });
      }
      onCreated();
      onClose();
    } catch (err) {
      toast({ title: "Erreur", description: err?.response?.data?.detail || "Création impossible", variant: "destructive" });
    } finally { setLoading(false); }
  };

  const Tab = ({ id, icon: Icon, label }) => (
    <button type="button" onClick={() => setKind(id)} data-testid={`quick-create-kind-${id}`}
      className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-semibold rounded-md ${kind === id ? "bg-brand text-white" : "text-neutral-600 hover:bg-neutral-100"}`}>
      <Icon size={15} /> {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="quick-create-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg p-5 shadow-2xl">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-heading font-bold text-lg text-neutral-900">Créer sur ce créneau</h3>
            <p className="text-sm text-neutral-500 capitalize" data-testid="quick-create-date">{fmt(date)}</p>
            {holiday && <p className="text-xs font-semibold text-amber-700 flex items-center gap-1 mt-1" data-testid="quick-create-holiday"><AlertTriangle size={13} /> Jour férié : {holiday} — vérifiez la disponibilité de l'équipe.</p>}
          </div>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-neutral-700"><X size={18} /></button>
        </div>
        <div className="flex gap-1 bg-neutral-50 border border-neutral-200 rounded-lg p-1 mb-4">
          <Tab id="rdv" icon={CalendarClock} label="Rendez-vous" />
          <Tab id="intervention" icon={MapPin} label="Intervention" />
        </div>

        {kind === "rdv" ? (
          <div className="space-y-3">
            <input required placeholder="Titre du RDV *" className={inputCls} value={rdv.titre} data-testid="quick-rdv-titre" onChange={(e) => setRdv({ ...rdv, titre: e.target.value })} />
            <select className={inputCls} value={rdv.prospect_id} onChange={(e) => setRdv({ ...rdv, prospect_id: e.target.value })}>
              <option value="">Prospect (optionnel)</option>
              {prospects.map((p) => <option key={p.id} value={p.id}>{p.societe}</option>)}
            </select>
            <input placeholder="Notes" className={inputCls} value={rdv.notes} onChange={(e) => setRdv({ ...rdv, notes: e.target.value })} />
            <label className="flex items-center gap-2 text-sm text-neutral-700">
              <input type="checkbox" className="accent-brand w-4 h-4" checked={rdv.push_dolibarr} onChange={(e) => setRdv({ ...rdv, push_dolibarr: e.target.checked })} /> Ajouter à l'agenda Dolibarr
            </label>
          </div>
        ) : (
          <div className="space-y-3">
            <input required placeholder="Client *" className={inputCls} value={intv.client_nom} data-testid="quick-intv-client" onChange={(e) => setIntv({ ...intv, client_nom: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <input required placeholder="Adresse *" className={inputCls} value={intv.adresse} onChange={(e) => setIntv({ ...intv, adresse: e.target.value })} />
              <input required placeholder="Ville *" className={inputCls} value={intv.ville} onChange={(e) => setIntv({ ...intv, ville: e.target.value })} />
            </div>
            <input placeholder="Prestation (ex: entretien bureaux)" className={inputCls} value={intv.prestation} onChange={(e) => setIntv({ ...intv, prestation: e.target.value })} />
            <div>
              <p className="text-xs font-semibold text-neutral-600 mb-1">Équipe</p>
              <div className="flex flex-wrap gap-2">
                {team.length === 0 && <span className="text-xs text-neutral-400">Aucun utilisateur synchronisé</span>}
                {team.map((t) => {
                  const on = intv.collaborateurs.some((c) => c.id === t.id);
                  return (
                    <button type="button" key={t.id} onClick={() => toggleCollab(t)} className={`text-xs px-2.5 py-1 rounded-full border ${on ? "bg-brand text-white border-brand" : "border-neutral-300 text-neutral-600"}`}>{t.nom}</button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <button type="submit" disabled={loading} data-testid="quick-create-submit" className="mt-4 w-full flex items-center justify-center gap-2 bg-brand hover:bg-brand-dark text-white font-semibold py-2.5 rounded-md disabled:opacity-60">
          {loading && <Loader2 size={15} className="animate-spin" />} {kind === "rdv" ? "Planifier le RDV" : "Planifier l'intervention"}
        </button>
      </form>
    </div>
  );
};

export default QuickCreate;
